/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Endless Ways to Swap Faces - 9 use case cards grid for Face Swap SEO page
 */
import Image from 'next/image';
import React from 'react';

const ASSET_PREFIX = 'https://assets.falcocut.ai/face-swap/';

interface UseCaseCard {
  imageUrl: string;
  title: string;
  description: string;
}

const useCases: UseCaseCard[] = [
  {
    imageUrl: `${ASSET_PREFIX}selfie-face-swap.webp`,
    title: 'Selfie Face Swap',
    description:
      'Swap your own short selfie videos to test new looks, hairstyles, or styles for fun and creative experiments.',
  },
  {
    imageUrl: `${ASSET_PREFIX}friends-family-face-swap.webp`,
    title: 'Friends & Family Swap',
    description:
      'Edit group videos by replacing faces of friends or family with ease. Great for playful surprises or fixing awkward moments.',
  },
  {
    imageUrl: `${ASSET_PREFIX}batch-face-swap.webp`,
    title: 'Batch Face Swap',
    description:
      'Apply one face swap across multiple videos or scenes at once. Save time and create consistent results for projects or campaigns.',
  },
  {
    imageUrl: `${ASSET_PREFIX}meme-face-swap.webp`,
    title: 'Meme Face Swap',
    description:
      'Turn your videos or clips into viral memes by swapping faces into iconic internet moments or funny templates.',
  },
  {
    imageUrl: `${ASSET_PREFIX}celebrity-face-swap.webp`,
    title: 'Celebrity Face Swap',
    description:
      'See yourself as a singer, movie star, or influencer by swapping faces with celebrities in videos.',
  },
  {
    imageUrl: `${ASSET_PREFIX}gender-face-swap.webp`,
    title: 'Gender Face Swap',
    description:
      'Try out a different gender virtually. Explore new looks or just have fun with creative transformations.',
  },
  {
    imageUrl: `${ASSET_PREFIX}e-commerce-face-swap.webp`,
    title: 'E-Commerce Face Swap',
    description:
      'Replace the face of a person in product or marketing videos to localize content, personalize ads, or adapt campaigns.',
  },
  {
    imageUrl: `${ASSET_PREFIX}video-face-swap.webp`,
    title: 'Video Face Swap',
    description:
      "Replace faces in videos with high accuracy, whether it's for fun clips, ads, or professional storytelling.",
  },
  {
    imageUrl: `${ASSET_PREFIX}face-morph.webp`,
    title: 'Face Morph',
    description:
      'Blend two faces into a smooth transition effect. Perfect for creative edits, transformations, or special projects.',
  },
];

const UseCaseCardItem: React.FC<UseCaseCard> = ({ imageUrl, title, description }) => (
  <div className="flex flex-col">
    <div className="relative w-full rounded-2xl overflow-hidden bg-[#F8F9FC] mb-3 pad:mb-4">
      <Image
        src={imageUrl}
        alt={title}
        width={380}
        height={380}
        className="w-full h-auto object-cover"
        loading="lazy"
      />
    </div>
    <h3 className="text-base pad:text-lg font-bold text-gray-900 mb-1 pad:mb-2">
      {title}
    </h3>
    <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
  </div>
);

const UseCasesSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Endless Ways to Swap Faces with AI
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          From lighthearted selfies and meme creations to professional headshots and
          marketing campaigns, our AI Face Swap tool unlocks countless possibilities.
          Whether you want to laugh with friends, see yourself as a celebrity, or create
          polished business visuals, you can explore 9 creative use cases—all with just a
          few clicks.
        </p>

        {/* 3x3 Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-6 pad:gap-8">
          {useCases.map((useCase, index) => (
            <UseCaseCardItem key={index} {...useCase} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
